/* eslint-disable no-bitwise */
/* eslint-disable no-console */
import { IEnterprisePayment } from '@efex-services/payments/types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clientSvc from '@efex-services/client/clientSvc';
import { useApiCall } from 'use-api-call';
import { selectBusiness } from '@pages/profile/store/selectors';
import { selectWalletsData } from '@/modules/wallets/selectors';
import { ForwardPayload } from './useForward.type';

interface IUseForwardService {
  payment: IEnterprisePayment;
  onClose: () => void;
  refetchPayments: () => void;
}
interface IUseForwardServiceReturn {
  showConfirmDialog: boolean;
  forwardPayedLoading: boolean;
  setShowConfirmDialog: (value: boolean) => void;
  handleConfirm: () => void;
  handleReject: () => void;
}

const useForwardService = ({
  payment,
  onClose,
  refetchPayments,
}: IUseForwardService): IUseForwardServiceReturn => {
  const walletsState = useSelector(selectWalletsData);
  const company = useSelector(selectBusiness);

  const { enterpriseId } = company || {};

  const {
    data: forwardPayedData,
    error: forwardPayedError,
    loading: forwardPayedLoading,
    invoke: payForward,
  } = useApiCall(({ forwardId, values }: { forwardId: string; values: any }) =>
    clientSvc.payForward({
      enterpriseId: enterpriseId ?? '',
      forwardId,
      values,
    })
  );

  const walletFromPayment = walletsState?.find(
    (wallet) =>
      wallet.currency.name === payment.forwardRemainingInfo?.desCurrency
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const generateGUID = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  const generatePayload = (): ForwardPayload => ({
    reference: payment.uuid,
    walletBalance: walletFromPayment?.uuid || '',
    amount: payment.forwardRemainingInfo?.remainingAmount || 0,
    requestId: generateGUID(),
  });

  const handleConfirm = () => {
    const forwardPayload = generatePayload();
    payForward({ forwardId: payment.uuid, values: forwardPayload });
  };

  const handleReject = () => {
    setShowConfirmDialog(false);
  };

  useEffect(() => {
    if (forwardPayedData && !forwardPayedLoading) {
      refetchPayments();
      onClose();
    }
  }, [
    forwardPayedData,
    forwardPayedError,
    forwardPayedLoading,
    onClose,
    refetchPayments,
  ]);

  return {
    showConfirmDialog,
    forwardPayedLoading,
    setShowConfirmDialog,
    handleConfirm,
    handleReject,
  };
};

export default useForwardService;
